/**
 * API Service
 * Firebase Functions 호출 래퍼
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

// 타입 정의
export interface SignupData {
  email: string;
  password: string;
  displayName: string;
  phoneNumber?: string;
  referralCode?: string;
}

export interface LoginData {
  email: string;
  password: string;
}

export interface FortuneInputBase {
  name: string;
  birthDate: string; // YYYY-MM-DD
}

export interface SajuInput extends FortuneInputBase {
  birthTime: string; // HH:MM
  gender: 'male' | 'female';
}

export interface TojungInput extends FortuneInputBase {
  lunarCalendar: boolean;
  targetYear: number;  // 운세를 볼 년도 (올해 또는 내년)
}

export interface CompatibilityInput extends FortuneInputBase {
  partnerName: string;
  partnerBirthDate: string;
}

export interface WealthInput extends FortuneInputBase {
  jobType: string;
}

export interface LoveInput extends FortuneInputBase {
  gender: 'male' | 'female';
  relationshipStatus: 'single' | 'dating' | 'married' | 'divorced';
}

export interface PaymentData {
  paymentType: 'oneTime' | 'subscription';
  productType: string;
  amount: number;
}

export interface GetReferralHistoryParams {
  startDate?: string;
  endDate?: string;
  referrerUid?: string;
  page?: number;
  limit?: number;
}

export interface ReferralHistoryItem {
  creditId: string;
  referrer: {
    uid: string;
    email: string;
    displayName: string;
    memberGrade: string;
  };
  referred: {
    uid: string;
    email: string;
    displayName: string;
    memberGrade: string;
  };
  payment: {
    paymentId: string;
    productName: string;
    amount: number;
  };
  creditAmount: number;
  percentage: number;
  createdAt: string;
  status: string;
}

export interface GetReferralHistoryResponse {
  success: boolean;
  data: {
    credits: ReferralHistoryItem[];
    totalCount: number;
    currentPage: number;
    totalPages: number;
  };
}

export interface CreatePaymentResponse {
  success: boolean;
  data: {
    paymentId: string;
    merchantUid: string;
    productName: string;
    amount: number;
  };
}

export interface VerifyPaymentResponse {
  success: boolean;
  data: {
    success: boolean;
    paymentId: string;
    merchantUid: string;
    amount: number;
    status: string;
  };
}

export interface CompletePaymentResponse {
  success: boolean;
  data: {
    success: boolean;
    paymentId: string;
    paymentType: 'oneTime' | 'subscription';
    productType: string;
  };
}

// 인증 API
export const authAPI = {
  signup: async (data: SignupData) => {
    const fn = httpsCallable(functions, 'authSignup');
    const result = await fn(data);
    return result.data;
  },

  login: async (data: LoginData) => {
    const fn = httpsCallable(functions, 'authLogin');
    const result = await fn(data);
    return result.data;
  },

  verifyToken: async (idToken: string) => {
    const fn = httpsCallable(functions, 'authVerifyToken');
    const result = await fn({ idToken });
    return result.data;
  }
};

// 운세 API
export const fortuneAPI = {
  today: async (data: FortuneInputBase) => {
    const fn = httpsCallable(functions, 'generateTodayFortune');
    const result = await fn(data);
    return result.data;
  },

  saju: async (data: SajuInput) => {
    const fn = httpsCallable(functions, 'generateSajuAnalysis');
    const result = await fn(data);
    return result.data;
  },

  tojung: async (data: TojungInput) => {
    const fn = httpsCallable(functions, 'generateTojungSecret');
    const result = await fn(data);
    return result.data;
  },

  compatibility: async (data: CompatibilityInput) => {
    const fn = httpsCallable(functions, 'generateCompatibility');
    const result = await fn(data);
    return result.data;
  },

  wealth: async (data: WealthInput) => {
    const fn = httpsCallable(functions, 'generateWealthFortune');
    const result = await fn(data);
    return result.data;
  },

  love: async (data: LoveInput) => {
    const fn = httpsCallable(functions, 'generateLoveFortune');
    const result = await fn(data);
    return result.data;
  }
};

// 결제 API
export const paymentAPI = {
  create: async (data: PaymentData): Promise<CreatePaymentResponse> => {
    const fn = httpsCallable(functions, 'createPayment');
    const result = await fn(data);
    return result.data as CreatePaymentResponse;
  },

  verify: async (paymentId: string, merchantUid: string): Promise<VerifyPaymentResponse> => {
    const fn = httpsCallable(functions, 'verifyPayment');
    const result = await fn({ paymentId, merchantUid });
    return result.data as VerifyPaymentResponse;
  },

  complete: async (paymentId: string, merchantUid: string): Promise<CompletePaymentResponse> => {
    const fn = httpsCallable(functions, 'completePayment');
    const result = await fn({ paymentId, merchantUid });
    return result.data as CompletePaymentResponse;
  }
};

// 관리자 API
export const adminAPI = {
  // 사용자 목록 조회
  getUsersList: async (params?: { memberGrade?: string; limit?: number; startAfter?: string }) => {
    const fn = httpsCallable(functions, 'getUsersList');
    const result = await fn(params);
    return result.data;
  },

  // 사용자 상세 정보
  getUserDetail: async (targetUid: string) => {
    const fn = httpsCallable(functions, 'getUserDetail');
    const result = await fn({ targetUid });
    return result.data;
  },

  // 사용자 등급 변경
  updateUserGrade: async (targetUid: string, newGrade: string) => {
    const fn = httpsCallable(functions, 'updateUserGrade');
    const result = await fn({ targetUid, newGrade });
    return result.data;
  },

  // 통계 조회
  getAnalytics: async () => {
    const fn = httpsCallable(functions, 'getAnalytics');
    const result = await fn();
    return result.data;
  },

  // 결제 내역 조회
  getPaymentsList: async (params?: { page?: number; limit?: number; status?: string }) => {
    const fn = httpsCallable(functions, 'getPaymentsList');
    const result = await fn(params);
    return result.data;
  },

  // Custom Claims 동기화 (임시 부트스트랩)
  syncCustomClaims: async () => {
    const fn = httpsCallable(functions, 'syncCustomClaims');
    const result = await fn();
    return result.data;
  },

  // 시스템 설정 초기화 (한 번만 실행)
  initSystemConfig: async () => {
    const fn = httpsCallable(functions, 'initSystemConfig');
    const result = await fn();
    return result.data;
  },

  // 정산비율 조회
  getReferralRates: async () => {
    const fn = httpsCallable(functions, 'getReferralRates');
    const result = await fn();
    return result.data;
  },

  // 정산비율 업데이트
  updateReferralRates: async (regular: number, special: number) => {
    const fn = httpsCallable(functions, 'updateReferralRates');
    const result = await fn({ regular, special });
    return result.data;
  },

  // 가격 설정 조회
  getPricing: async () => {
    const fn = httpsCallable(functions, 'getPricing');
    const result = await fn();
    return result.data;
  },

  // 가격 설정 업데이트
  updatePricing: async (servicePrices: Record<string, number>, subscriptionPrices: Record<string, number>) => {
    const fn = httpsCallable(functions, 'updatePricing');
    const result = await fn({ servicePrices, subscriptionPrices });
    return result.data;
  },

  // 가격 설정 초기화
  initPricingConfig: async () => {
    const fn = httpsCallable(functions, 'initPricingConfig');
    const result = await fn();
    return result.data;
  },

  // 공개 가격 설정 조회 (인증된 사용자 누구나)
  getPublicPricing: async () => {
    const fn = httpsCallable(functions, 'getPublicPricing');
    const result = await fn();
    return result.data;
  },

  // 레퍼럴 발생이력 조회
  getReferralHistory: async (params?: GetReferralHistoryParams): Promise<GetReferralHistoryResponse> => {
    const fn = httpsCallable(functions, 'getReferralHistory');
    const result = await fn(params);
    return result.data as GetReferralHistoryResponse;
  }
};